import type { DemoEmbedViewportMode } from "@/lib/security/demo-embed-proxy";

/** Matches mobile [LivePreviewScrollHintController.handHintDuration]. */
export const LIVE_PREVIEW_SCROLL_HINT_HAND_DURATION_MS = 1160;

/** Delay after iframe load before nudge (matches mobile). */
export const LIVE_PREVIEW_SCROLL_HINT_INITIAL_DELAY_MS = 450;

function buildLivePreviewScrollHintJs(forceReplay: boolean): string {
	return `
(function(){
  if (${forceReplay ? "false" : "window.__techxeedLivePreviewScrollHint"}) return;
  window.__techxeedLivePreviewScrollHint = true;
  function scrollRoot(){
    return document.scrollingElement || document.documentElement || document.body;
  }
  function ease(t){
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  }
  function animateScroll(el, to, duration, done){
    var from = el.scrollTop || 0;
    var start = performance.now();
    function frame(now){
      var t = Math.min(1, (now - start) / duration);
      el.scrollTop = from + (to - from) * ease(t);
      if (t < 1) requestAnimationFrame(frame);
      else if (done) done();
    }
    requestAnimationFrame(frame);
  }
  function run(){
    var el = scrollRoot();
    if (!el) return;
    var max = Math.max(0, (el.scrollHeight || 0) - (el.clientHeight || 0));
    if (max < 48) return;
    var origin = el.scrollTop || 0;
    var delta = Math.min(160, Math.max(72, max * 0.18));
    var target = Math.min(origin + delta, max);
    if (target <= origin + 8) return;
    animateScroll(el, target, 480, function(){
      setTimeout(function(){ animateScroll(el, origin, 520, null); }, 160);
    });
  }
  setTimeout(run, 350);
})();
`;
}

const shownHintKeys = new Set<string>();

export function livePreviewScrollHintKey(
	demoSrc: string,
	viewport: DemoEmbedViewportMode,
	scope = "default",
): string {
	return `${demoSrc}:${viewport}:${scope}`;
}

/** In-page scroll nudge (same-origin demo embed iframe only). */
export function runLivePreviewScrollHintInDocument(
	doc: Document,
	options?: { forceReplay?: boolean },
): void {
	try {
		const script = doc.createElement("script");
		script.textContent = buildLivePreviewScrollHintJs(options?.forceReplay === true);
		(doc.head ?? doc.documentElement).appendChild(script);
		script.remove();
	} catch {
		/* cross-origin or sandbox — ignore */
	}
}

function delay(ms: number): Promise<void> {
	return new Promise((resolve) => {
		window.setTimeout(resolve, ms);
	});
}

/**
 * Hand overlay + same-origin scroll nudge after live preview load.
 * Default: once per demo URL + viewport + scope per tab.
 * `forceReplay`: always run (used for the enlarged Live Preview modal).
 */
export async function runLivePreviewScrollHintAfterLoad(options: {
	iframe: HTMLIFrameElement | null;
	demoSrc: string;
	embedViewport: DemoEmbedViewportMode;
	restrictDemoNavigation: boolean;
	onHandHintStart: () => void;
	onHandHintEnd: () => void;
	/** Distinguishes inline media vs enlarged modal so each can show the hint. */
	hintScope?: string;
	/** Ignore the once-per-tab guard (enlarged modal should always hint on open). */
	forceReplay?: boolean;
}): Promise<void> {
	const scope = options.hintScope ?? "default";
	const forceReplay = options.forceReplay === true;
	const key = livePreviewScrollHintKey(options.demoSrc, options.embedViewport, scope);

	if (!forceReplay) {
		if (shownHintKeys.has(key)) return;
		shownHintKeys.add(key);
	}

	await delay(LIVE_PREVIEW_SCROLL_HINT_INITIAL_DELAY_MS);

	options.onHandHintStart();

	if (options.restrictDemoNavigation) {
		try {
			const doc = options.iframe?.contentDocument;
			if (doc) runLivePreviewScrollHintInDocument(doc, { forceReplay });
		} catch {
			/* ignore */
		}
	}

	await delay(LIVE_PREVIEW_SCROLL_HINT_HAND_DURATION_MS);
	options.onHandHintEnd();
}
